import { invalidate } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import { BufferAttribute, BufferGeometry, LineBasicMaterial } from 'three';

import type { Point3D } from 'api/models';
import type { DimensionPanel } from 'app/modules/wallforming/components/scenes/objects/dimensions/types/dimensionPanel';

const INITIAL_CAPACITY = 64;
const DEFAULT_OFFSET = 0.006;
const VERTS_PER_PANEL = 8;

const lineMaterial = new LineBasicMaterial({ color: 0x000000, depthWrite: true, depthTest: true });

interface PanelDimensionBorderProps {
    panels: DimensionPanel[];
}

export const PanelDimensionBorder = ({ panels }: PanelDimensionBorderProps) => {
    const capacityRef = useRef<number>(INITIAL_CAPACITY);

    const geometry = useMemo(() => {
        const geo = new BufferGeometry();
        geo.setAttribute('position', new BufferAttribute(new Float32Array(INITIAL_CAPACITY * VERTS_PER_PANEL * 3), 3, false));
        return geo;
    }, []);

    // cleanup on unmount
    useEffect(() => {
        return () => {
            geometry?.dispose();
            lineMaterial.dispose();
        };
    }, [geometry]);

    useEffect(() => {
        const newCount = panels.length;

        if (newCount === 0) {
            geometry.setDrawRange(0, 0);
            geometry.computeBoundingSphere();
            invalidate();
            return;
        }

        // resize buffer if needed
        if (newCount > capacityRef.current) {
            capacityRef.current = Math.max(newCount, capacityRef.current * 2);
            geometry.setAttribute(
                'position',
                new BufferAttribute(new Float32Array(capacityRef.current * VERTS_PER_PANEL * 3), 3, false)
            );
        }

        // update positions for all panels: always draw all four borders
        const attribute = geometry.getAttribute('position') as BufferAttribute;
        const positions = attribute.array as Float32Array;
        let vi = 0;

        for (let i = 0; i < newCount; i++) {
            const panel = panels[i];
            // apply normal offset to place the lines in front of the wall
            const center: Point3D = {
                x: panel.center.x + panel.normal.x * DEFAULT_OFFSET,
                y: panel.center.y + panel.normal.y * DEFAULT_OFFSET,
                z: panel.center.z + panel.normal.z * DEFAULT_OFFSET
            };

            const halfWidth = panel.width / 2;
            const halfHeight = panel.height / 2;

            const bottomLeft: Point3D = {
                x: center.x - panel.u.x * halfWidth - panel.v.x * halfHeight,
                y: center.y - panel.u.y * halfWidth - panel.v.y * halfHeight,
                z: center.z - panel.u.z * halfWidth - panel.v.z * halfHeight
            };

            const bottomRight: Point3D = {
                x: center.x + panel.u.x * halfWidth - panel.v.x * halfHeight,
                y: center.y + panel.u.y * halfWidth - panel.v.y * halfHeight,
                z: center.z + panel.u.z * halfWidth - panel.v.z * halfHeight
            };

            const topLeft: Point3D = {
                x: center.x - panel.u.x * halfWidth + panel.v.x * halfHeight,
                y: center.y - panel.u.y * halfWidth + panel.v.y * halfHeight,
                z: center.z - panel.u.z * halfWidth + panel.v.z * halfHeight
            };

            const topRight: Point3D = {
                x: center.x + panel.u.x * halfWidth + panel.v.x * halfHeight,
                y: center.y + panel.u.y * halfWidth + panel.v.y * halfHeight,
                z: center.z + panel.u.z * halfWidth + panel.v.z * halfHeight
            };

            // left edge
            positions[vi++] = bottomLeft.x;
            positions[vi++] = bottomLeft.y;
            positions[vi++] = bottomLeft.z;
            positions[vi++] = topLeft.x;
            positions[vi++] = topLeft.y;
            positions[vi++] = topLeft.z;

            // right edge
            positions[vi++] = bottomRight.x;
            positions[vi++] = bottomRight.y;
            positions[vi++] = bottomRight.z;
            positions[vi++] = topRight.x;
            positions[vi++] = topRight.y;
            positions[vi++] = topRight.z;

            // bottom edge
            positions[vi++] = bottomLeft.x;
            positions[vi++] = bottomLeft.y;
            positions[vi++] = bottomLeft.z;
            positions[vi++] = bottomRight.x;
            positions[vi++] = bottomRight.y;
            positions[vi++] = bottomRight.z;

            // top edge
            positions[vi++] = topLeft.x;
            positions[vi++] = topLeft.y;
            positions[vi++] = topLeft.z;
            positions[vi++] = topRight.x;
            positions[vi++] = topRight.y;
            positions[vi++] = topRight.z;
        }

        attribute.needsUpdate = true;
        geometry.setDrawRange(0, newCount * VERTS_PER_PANEL);
        geometry.computeBoundingSphere();
        invalidate();
    }, [geometry, panels]);

    return <lineSegments frustumCulled={false} geometry={geometry} material={lineMaterial} renderOrder={100} />;
};
